import { useEffect, useRef, useState } from 'react';
import MobilniPanel from './MobilniPanel';

/**
 * ModulLayout — STANDARDNÍ ROZVRŽENÍ MODULU SE SEZNAMEM (norma FofrLama OS).
 *
 * Pravidlo, které platí napříč aplikací:
 *
 *   1) Nic není vybráno → seznam přes **celou šířku**. Je to přehled, takže
 *      řádky můžou nést víc informací (sloupce navíc). Žádná poloprázdná
 *      obrazovka s hláškou „vyberte položku" vedle úzkého sloupce.
 *   2) Po výběru položky → seznam se zúží na navigační šířku a vedle něj se
 *      otevře **detail**, případně ještě **panel** (třetí sloupec) s tím, co
 *      k položce jen informuje (kalkulace, revize, statistika).
 *
 * Kolik sloupců modul otevírá, si určuje modul sám — proto je `panel` prop:
 * kde ho modul předá, tam je standardně otevřený; kde ne, jsou sloupce dva.
 * Panel se schová, když na něj není místo (`panelOd`) a na mobilu vždy.
 *
 * ## Mobil
 *
 * Vždy jen jeden sloupec: seznam, nebo detail. Se zapnutým `mobilRezim` (norma)
 * je z toho **třístupňové zobrazení**:
 *
 *   1) **seznam přes celou obrazovku** — je to navigace modulu; hlavička seznamu
 *      (hledání, filtr, pohledy) zůstává na místě, scrollují jen řádky
 *   2) **detail** — ze seznamu vyjede zprava, zpátky vede „Zpět" v jeho hlavičce
 *   3) **panel** — vyjede zpod pravé hrany přejetím nebo klepnutím na úchyt
 *      (viz `MobilniPanel`)
 *
 * `mobilRezim` je **dobrovolný**: bez něj se kreslí to co dřív, ať se moduly
 * dají překlápět po jednom. Zapnutý taky spravuje rozvržení výšky — bez něj je
 * mobilní obal obyčejný blok, takže vnitřní `flex: 1` sloupců nemá o co se
 * opřít a obsah se místo scrollování ořízne.
 *
 * @param {boolean} props.isMobile
 * @param {boolean} props.vybrano - je vybraná položka? (řídí přehled × detail)
 * @param {React.ReactNode|(stav: {siroky: boolean}) => React.ReactNode} props.seznam
 *        Seznam. Jako funkce dostane `siroky` = běží v režimu přehledu.
 * @param {React.ReactNode|() => React.ReactNode} props.detail - obsah druhého sloupce.
 *        Jako funkce se vyhodnotí, až když je co ukázat (nic nevybráno = nestaví se).
 * @param {React.ReactNode|() => React.ReactNode} [props.panel] - třetí sloupec;
 *        když je prázdný, sloupec se nekreslí a modul má dva.
 * @param {boolean} [props.panelVzdy=false] - panel patří ke KONTEXTU, ne k vybrané
 *        položce, takže se kreslí i v přehledu. Pro modul, jehož panel odpovídá
 *        na otázku, která platí bez ohledu na výběr: v Bance je to účet (zůstatek,
 *        měsíc, „K dořešení"). Bez toho by ta čísla musela nad seznam a ukrajovala
 *        by mu výšku pokaždé — v Bance osm viditelných pohybů místo dvanácti.
 *        Zákaz z normy tím neutrpí: ten míří na poloprázdnou plochu s hláškou
 *        „vyberte položku", ne na panel se živými čísly.
 * @param {number} [props.panelSirka=320]
 * @param {number} [props.panelOd=1280] - od jaké šířky okna se panel vejde
 * @param {{ideal: string, min: number, max: number}} [props.seznamSirka]
 * @param {boolean} [props.mobilRezim=false] - zapnout třístupňový mobil
 * @param {boolean} [props.mobilPanel=true] - má aktuální položka co ukázat v panelu?
 *        (u nové, ještě neuložené, nemá — úchyt se pak nekreslí)
 * @param {string} [props.mobilPanelTitulek]
 * @param {Function} [props.mobilPanelIkona] - lucide ikona do úchytu panelu
 * @param {string} [props.mobilRezervaDole] - místo pod obsahem panelu (Palec obalu)
 * @param {string} [props.barva] - akcentová barva modulu (úchyt panelu)
 * @param {boolean} [props.isDark]
 */
export const SEZNAM_SIRKA = { ideal: '25vw', min: 320, max: 520 };
// Třetí sloupec (panel) — šířka a od jaké šířky okna se vejde. Exportované,
// ať stránky se stejným třísloupcovým rozvržením (např. Přehled) sedí na pixel.
export const PANEL_SIRKA = 320;
export const PANEL_OD = 1280;

export default function ModulLayout({
    isMobile = false,
    vybrano = false,
    seznam,
    detail = null,
    panel = null,
    panelVzdy = false,
    panelSirka = PANEL_SIRKA,
    panelOd = PANEL_OD,
    seznamSirka = SEZNAM_SIRKA,
    mobilRezim = false,
    mobilPanel = true,
    mobilPanelTitulek = 'Statistika',
    mobilPanelIkona = null,
    mobilRezervaDole = null,
    barva = '#F59E0B',
    isDark = false,
}) {
    // Vejde se třetí sloupec? Sleduje se tady, ne v modulu — ať se pravidlo
    // nemusí opisovat v každé stránce.
    const [vejdeSePanel, setVejdeSePanel] = useState(() => window.innerWidth >= panelOd);
    useEffect(() => {
        const h = () => setVejdeSePanel(window.innerWidth >= panelOd);
        h();
        window.addEventListener('resize', h);
        return () => window.removeEventListener('resize', h);
    }, [panelOd]);

    const prehled = !vybrano;                                   // seznam přes celou šířku
    const stupne = isMobile && mobilRezim;                      // třístupňový mobil

    // Panel na mobilu není sloupec, ale zásuvka — otevřený stav drží layout,
    // ať se zavře sám, kdykoli se zavře detail (jinak by visel nad seznamem).
    const [panelOtevren, setPanelOtevren] = useState(false);
    const plochaRef = useRef(null);
    useEffect(() => { if (prehled) setPanelOtevren(false); }, [prehled]);

    // Sloupce se vyhodnocují líně — dokud není nic vybráno, detail se vůbec
    // nestaví (je to největší kus stránky a bez položky nemá z čeho).
    const vyhodnot = (v, arg) => (typeof v === 'function' ? v(arg) : v);

    // Mobil = jeden sloupec: buď seznam, nebo detail.
    const zobrazSeznam = !isMobile || prehled;
    const zobrazDetail = !prehled;

    const obsahSeznamu = zobrazSeznam ? vyhodnot(seznam, { siroky: prehled && !isMobile }) : null;
    const obsahDetailu = zobrazDetail ? vyhodnot(detail) : null;
    const obsahPanelu = (!isMobile && (panelVzdy || !prehled) && vejdeSePanel) ? vyhodnot(panel) : null;
    const zobrazPanel = !!obsahPanelu;

    // Seznam mění šířku, ne způsob rozpínání — jedna animovatelná hodnota, aby
    // přechod přehled ↔ detail byl plynulý pohyb, ne skok.
    // S `panelVzdy` stojí v přehledu vedle seznamu ještě panel, takže „přes celou
    // šířku" znamená zbytek po něm — jinak by se sloupce sečetly přes plochu.
    // Pořád je to JEDNA animovatelná hodnota, aby přechod přehled ↔ detail
    // zůstal pohyb, ne skok (mezera sloupců je `gap-4`, tedy 16 px).
    const sirkaSeznamu = isMobile
        ? '100%'
        : (prehled
            ? (zobrazPanel ? `calc(100% - ${panelSirka + 16}px)` : '100%')
            : `clamp(${seznamSirka.min}px, ${seznamSirka.ideal}, ${seznamSirka.max}px)`);

    // Stupeň mobilu vyplní obal celý — jen tak má vnitřní `flex: 1` (hlavička
    // seznamu nahoře, scrollující řádky pod ní) podle čeho se měřit.
    const stupenStyl = { flex: 1, minWidth: 0, minHeight: 0 };

    return (
        <div ref={plochaRef}
            className={stupne ? 'flex flex-col' : (isMobile ? '' : 'flex gap-4')}
            style={{ flex: 1, minHeight: 0, overflow: 'hidden', ...(stupne ? { position: 'relative' } : null) }}>
            <style>{`
                .modul-seznam { transition: width 0.28s cubic-bezier(0.4, 0, 0.2, 1); }
                .modul-sloupec { animation: modul-sloupec-in 0.28s cubic-bezier(0.4, 0, 0.2, 1); }
                @keyframes modul-sloupec-in {
                    from { opacity: 0; transform: translateX(14px); }
                    to   { opacity: 1; transform: none; }
                }
                @media (prefers-reduced-motion: reduce) {
                    .modul-seznam { transition: none; }
                    .modul-sloupec { animation: none; }
                }
            `}</style>

            {zobrazSeznam && (
                <div className={stupne ? 'flex flex-col' : 'modul-seznam flex flex-col'}
                    style={stupne ? stupenStyl : { width: sirkaSeznamu, flexShrink: 0, minWidth: 0, minHeight: 0 }}>
                    {obsahSeznamu}
                </div>
            )}

            {zobrazDetail && (
                <div className={stupne ? 'modul-sloupec flex flex-col' : 'modul-sloupec flex-1 min-w-0 flex flex-col'}
                    style={stupne ? stupenStyl : { minHeight: 0 }}>
                    {obsahDetailu}
                </div>
            )}

            {zobrazPanel && (
                <div className="modul-sloupec flex flex-col gap-3" style={{ width: panelSirka, flexShrink: 0, minHeight: 0, overflowY: 'auto' }}>
                    {obsahPanelu}
                </div>
            )}

            {/* Třetí stupeň mobilu — zásuvka zpod pravé hrany nad detailem */}
            {stupne && !prehled && panel && mobilPanel && (
                <MobilniPanel
                    plochaRef={plochaRef}
                    otevreno={panelOtevren}
                    onOtevri={() => setPanelOtevren(true)}
                    onZavri={() => setPanelOtevren(false)}
                    obsah={panel}
                    titulek={mobilPanelTitulek}
                    Ikona={mobilPanelIkona}
                    rezervaDole={mobilRezervaDole}
                    barva={barva}
                    isDark={isDark}
                />
            )}
        </div>
    );
}
